@file:NoLiveLiterals

package com.example.borshi.web.wallet

import androidx.compose.runtime.NoLiveLiterals
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.StateFlow

/**
 * Repository interface for wallet operations
 * Implementations handle communication with Firebase backend
 */
interface WalletRepository {

    /**
     * Observe the user's wallet in real-time
     * Returns null if wallet doesn't exist yet (will be created on first top-up)
     */
    fun observeWallet(): StateFlow<Wallet?>

    /**
     * Observe transaction history with pagination
     * @param limit Number of transactions per page
     * @param startAfter Timestamp of last transaction from previous page (for pagination)
     */
    fun observeTransactions(
        limit: Int = 20,
        startAfter: Long? = null
    ): Flow<List<WalletTransaction>>

    /**
     * Get a single transaction by ID
     */
    suspend fun getTransaction(transactionId: String): Result<WalletTransaction>

    /**
     * Request a top-up (add money to wallet)
     * This creates an intent and should be followed by payment gateway integration
     *
     * @param userId User ID
     * @param amount Amount to add
     * @param idempotencyKey Unique key to prevent duplicate requests
     * @param paymentMethodId Payment method (gateway-specific)
     * @return Result with transaction ID and payment intent
     */
    suspend fun requestTopUp(
        userId: String,
        amount: Money,
        idempotencyKey: String,
        paymentMethodId: String? = null
    ): Result<TopUpIntent>

    /**
     * Capture/complete a top-up after payment gateway confirms payment
     * This should be called by backend after payment gateway webhook
     * Client can poll or listen for status updates
     *
     * @param topUpIntentId The intent ID from requestTopUp
     * @return Result with completed transaction
     */
    suspend fun captureTopUp(topUpIntentId: String): Result<WalletTransaction>

    /**
     * Purchase a plan or product using wallet balance
     *
     * @param userId User ID
     * @param planId Plan or product ID
     * @param amount Amount to deduct
     * @param idempotencyKey Unique key to prevent duplicate purchases
     * @param metadata Additional context (order details, etc.)
     * @return Result with transaction and updated wallet
     */
    suspend fun purchaseWithWallet(
        userId: String,
        planId: String,
        amount: Money,
        idempotencyKey: String,
        metadata: Map<String, String> = emptyMap()
    ): Result<WalletOperationResult>

    /**
     * Refund a purchase back to wallet
     *
     * @param userId User ID
     * @param orderId Original order/purchase ID
     * @param amount Amount to refund
     * @param idempotencyKey Unique key to prevent duplicate refunds
     * @param reason Refund reason
     * @return Result with transaction and updated wallet
     */
    suspend fun refundToWallet(
        userId: String,
        orderId: String,
        amount: Money,
        idempotencyKey: String,
        reason: String = ""
    ): Result<WalletOperationResult>

    /**
     * Hold funds for a pending order (reserve balance)
     * Use this when order is created but not yet confirmed
     *
     * @param userId User ID
     * @param orderId Order ID
     * @param amount Amount to hold
     * @param idempotencyKey Unique key
     * @return Result with transaction
     */
    suspend fun holdFunds(
        userId: String,
        orderId: String,
        amount: Money,
        idempotencyKey: String
    ): Result<WalletOperationResult>

    /**
     * Release held funds back to available balance
     * Use this when an order is cancelled
     *
     * @param userId User ID
     * @param orderId Order ID
     * @param amount Amount to release
     * @param idempotencyKey Unique key
     * @return Result with transaction
     */
    suspend fun releaseFunds(
        userId: String,
        orderId: String,
        amount: Money,
        idempotencyKey: String
    ): Result<WalletOperationResult>

    /**
     * Capture held funds (finalize the hold)
     * Use this when an order is confirmed
     *
     * @param userId User ID
     * @param orderId Order ID
     * @param amount Amount to capture
     * @param idempotencyKey Unique key
     * @return Result with transaction
     */
    suspend fun captureFunds(
        userId: String,
        orderId: String,
        amount: Money,
        idempotencyKey: String
    ): Result<WalletOperationResult>
}

/**
 * Top-up intent created before payment
 */
data class TopUpIntent(
    val intentId: String,
    val userId: String,
    val amount: Money,
    val status: String, // "pending", "completed", "failed", "cancelled"
    val paymentGatewayReference: String? = null,
    val createdAt: Long,
    val expiresAt: Long // Intent expires after some time
)
